import React, { useState, useEffect, useRef, useCallback } from "react";
import { AutoComplete, Input, Form, notification } from "antd";
import { CloseCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import axios from "axios";
import getGoogleMapsPlacesApiClient from "../../utils/GoogleApiClient.js";
import googleMapsLogo from "../../Assets/GoogleMaps_Logo_White.svg";

// Props:
// - record: the row data
// - dataIndex: the field name
// - form: AntD form instance
// - handleOnAddressSelected: your global address selection function
// - loadSuggestions: your autocomplete suggestions loader
// - addressPredictionOptions: current autocomplete options
const AddressCell = ({ record, dataIndex, form }) => {
	// Local value to avoid losing focus
	const [localValue, setLocalValue] = useState(record[dataIndex] || "");
	const [autoCompleteLoading, setAutoCompleteLoading] = useState(false);
	const [addressPredictionOptions, setAddressPredictionOptions] = useState([]);
	const googleSessionTokenRef = useRef();
	const addressComponentId = `${record.key}-${dataIndex}`;
	const timeoutRef = useRef();
	const searchGoogleApiForAddress = async (address) => {
		const places = await getGoogleMapsPlacesApiClient();
		if (!googleSessionTokenRef.current) {
			googleSessionTokenRef.current = new places.AutocompleteSessionToken();
		}
		const googleAddresses =
			await new places.AutocompleteService().getPlacePredictions({
				input: address,
				sessionToken: googleSessionTokenRef.current,
			});
		const formattedPrediction = {
			label: googleAddresses.predictions[0].description,
			value: googleAddresses.predictions[0].description,
			...googleAddresses.predictions[0],
		};
		return formattedPrediction;
	};
	/**
	 * @param {string} url
	 * @param {{method:string, params:{lat:number,long:number}}} options
	 * @returns {Promise<Object>}
	 */
	async function gcpRequest(url, options) {
		try {
			const res = await axios.request({
				method: options.method,
				baseURL: "https://maps.googleapis.com/maps/api/",
				url: url,
				params: {
					location: `${options.params.lat},${options.params.long}`,
					timestamp: Math.floor(new Date().getTime() / 1000),
					key: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
				},
			});
			if (res.data.error) {
				notification.error({
					message: res.data.message,
					placement: "top",
					showProgress: true,
					pauseOnHover: true,
				});
				throw res;
			}
			return res.data;
		} catch (error) {
			throw error;
		}
	}
	async function getAddressTimeZone(lat, long) {
		const response = await gcpRequest("timezone/json", {
			method: "GET",
			params: { lat, long },
		});
		console.log(response);
		return response.timeZoneId;
	}
	const handleOnAddressSelected = async (value, option) => {
		const googleAddress = await searchGoogleApiForAddress(value);
		option.place_id = googleAddress.place_id;
		const places = await getGoogleMapsPlacesApiClient();

		// Clear the session token, it can only be used in one request
		const sessionToken = googleSessionTokenRef.current;
		googleSessionTokenRef.current = undefined;
		new places.PlacesService(
			document.getElementById(addressComponentId)
		).getDetails(
			{
				placeId: option.place_id,
				fields: [
					// you can pick the fields you want for your application
					// @see https://developers.google.com/maps/documentation/javascript/place-data-fields
					"formatted_address",
					"geometry.location",
					"address_components",
				],
				// pass the session token so all autocomplete requests are counted as part of this places request
				sessionToken,
			},
			async (place, status) => {
				if (status === places.PlacesServiceStatus.OK) {
					// set the place detail in this state component
					// you can use this info to show the detail in the UI, or maybe a checkmark
					setLocalValue(place.formatted_address);
					form.setFieldValue(dataIndex, place.formatted_address);
					form.setFieldValue(
						"timezone",
						await getAddressTimeZone(
							place.geometry.location.lat(),
							place.geometry.location.lng()
						)
					);
					//TODO could be the auto complete loading
					// notify up the tree that a location is selected
				} else {
					// silently fail here and track it with an error tracker like Sentry
					// or fail loudly if users are required to use a suggestion from the list
				}
			}
		);
	};
	const loadSuggestions = async (inputValue) => {
		clearTimeout(timeoutRef.current);

		// don't load suggestions if not enough characters
		if (!inputValue || inputValue.trim().length < 3) {
			setAddressPredictionOptions([]);
			return;
		}
		setAutoCompleteLoading(true);

		// debounce the loading of suggestions to reduce usage of the Google API
		timeoutRef.current = setTimeout(async () => {
			const places = await getGoogleMapsPlacesApiClient();
			if (!googleSessionTokenRef.current) {
				googleSessionTokenRef.current = new places.AutocompleteSessionToken();
			}

			// @see https://developers.google.com/maps/documentation/javascript/place-autocomplete
			new places.AutocompleteService().getPlacePredictions(
				{
					input: inputValue,
					sessionToken: googleSessionTokenRef.current,
				},
				(predictions, status) => {
					// when the status is 'ZERO_RESULTS', we treat it as if there are 0 suggestions, no error
					// you could change this behavior if you require users to pick a suggestion
					if (status === places.PlacesServiceStatus.ZERO_RESULTS) {
						setAddressPredictionOptions([]);
						return;
					}
					if (status !== places.PlacesServiceStatus.OK || !predictions) {
						// if you allow your users to enter arbitrary locations that aren't autocompleted,
						// then you can fail silently here and track the error (with Sentry for example)
						console.error("AutocompleteService error:", status);
						return;
					}
					const formattedPredictions = [];
					for (const predictionItem of predictions) {
						const formattedPrediction = {
							label: predictionItem.description,
							value: predictionItem.description,
							...predictionItem,
						};
						formattedPredictions.push(formattedPrediction);
					}
					setAutoCompleteLoading(false);
					setAddressPredictionOptions(formattedPredictions);
				}
			);
		}, 350);
	};
	// Keep in sync if record changes externally
	useEffect(() => {
		setLocalValue(record[dataIndex] || "");
	}, [record[dataIndex]]);

	// Ref to track if user is typing (prevents race conditions)
	const typingRef = useRef(false);

	const handleChange = (value) => {
		typingRef.current = true;
		setLocalValue(value);
		form.setFieldValue(dataIndex, value); // optional, keep form in sync
	};

	const handleSelect = async (value, option) => {
		setLocalValue(value);
		typingRef.current = false;
		await handleOnAddressSelected(value, option); // call your global function
	};

	const handleClear = () => {
		setLocalValue("");
		form.setFieldValue(dataIndex, null);
	};

	return (
		<AutoComplete
			key={`${record.key}-${dataIndex}`}
			notFoundContent={
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						padding: "8px",
						color: "#bcff00",
					}}
				>
					{autoCompleteLoading ? <LoadingOutlined spin /> : "No matches"}
				</div>
			}
			popupRender={(menu) => (
				<>
					{menu}
					<div
						style={{
							display: "flex",
							justifyContent: "right",
							alignItems: "center",
							padding: 8,
							paddingBottom: 2,
						}}
					>
						<img
							style={{
								marginTop: "6px",
								fontSize: "12px",
								color: "#888",
							}}
							alt="Google Maps attribution"
							src={googleMapsLogo}
						/>
					</div>
				</>
			)}
			value={form.getFieldValue("businessLocation") ?? localValue}
			options={addressPredictionOptions}
			onSelect={handleSelect}
			onChange={handleChange}
			onSearch={loadSuggestions}
			placeholder="Start typing..."
			id={`${record.key}-${dataIndex}`}
		>
			<Input
				allowClear={{
					clearIcon: (
						<CloseCircleOutlined
							style={{
								fontSize: "14px",
								color: "#fff",
								background: "transparent",
							}}
						/>
					),
				}}
				value={localValue}
				onChange={(e) => handleChange(e.target.value)}
				onClear={handleClear}
			/>
		</AutoComplete>
	);
};

export default AddressCell;
